<template>
  <div class="trail-scrubber" :style="positionStyle">
    <button type="button" class="scrub-btn" @click="toggle">{{ playing ? 'Pause' : 'Play' }}</button>
    <input
      class="scrub-range"
      type="range"
      :min="span ? span.start : 0"
      :max="span ? span.end : 0"
      step="1000"
      :value="cursorMs"
      @input="onSeek" />
    <span class="scrub-time">{{ label }}</span>
    <select class="scrub-speed form-control" v-model.number="rate">
      <option :value="1">1x</option>
      <option :value="4">4x</option>
      <option :value="16">16x</option>
    </select>
    <label class="scrub-follow">
      <input type="checkbox" v-model="follow" />
      <span>Follow</span>
    </label>
    <button type="button" class="scrub-btn scrub-exit" @click="$emit('exit', massId)">Exit</button>
  </div>
</template>

<script>
import { publish, HOVER } from '@/util/chart/chartSync';
import { formatDateTime } from '@/util/date';
import { timeRange } from '@/util/trail/trailPlayback';

export default {
  name: 'TrailScrubber',
  props: {
    // kanal hover halaman (voyage: hover:voyage)
    hoverChannel: {
      type: String,
      default: HOVER,
    },
    points: {
      type: Array,
      required: true,
    },
    massId: {
      type: [String, Number],
      default: null,
    },
    insetLeft: {
      type: Number,
      default: 0,
    },
    insetBottom: {
      type: Number,
      default: 0,
    },
    onFrame: {
      type: Function,
      default: null,
    },
    onEnd: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      cursorMs: 0,
      playing: false,
      rate: 1,
      follow: true,
      raf: null,
      lastFrame: 0,
      screenWidth: window.innerWidth,
    };
  },
  computed: {
    // Di peta pendek kolom kontrol Leaflet turun sampai bawah, jadi minggir 62px.
    positionStyle() {
      const minLeft = this.screenWidth <= 575 ? 62 : 12;
      return {
        left: `${Math.max(this.insetLeft + 12, minLeft)}px`,
        bottom: `${this.insetBottom + 26}px`,
      };
    },
    span() {
      return timeRange(this.points);
    },
    // Di 1x, satu jarak antar titik berlalu tiap detik.
    msPerSecond() {
      if (!this.span || this.points.length < 2) return 1000;
      return ((this.span.end - this.span.start) / (this.points.length - 1)) * this.rate;
    },
    // Tahun & offset dibuang; bar filter di atas sudah menyebut rentangnya.
    label() {
      if (!this.span) return '-';
      return formatDateTime(this.cursorMs, 'DD MMM HH:mm', null, false);
    },
  },
  watch: {
    // Rentang trail ganti: balik ke awal.
    points(next, prev) {
      const fingerprint = (p) => (Array.isArray(p) && p.length
        ? `${p.length}:${p[0].timestamp}:${p[p.length - 1].timestamp}`
        : '');
      if (fingerprint(next) === fingerprint(prev)) return;
      this.stop();
      this.cursorMs = this.span ? this.span.start : 0;
    },
  },
  mounted() {
    if (this.span) this.cursorMs = this.span.start;
    this._onResize = () => {
      this.screenWidth = window.innerWidth;
    };
    window.addEventListener('resize', this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
    this.stop();
    publish(this.hoverChannel, null);
    if (this.onEnd) this.onEnd(this.massId);
  },
  methods: {
    emitCursor() {
      publish(this.hoverChannel, this.cursorMs);
      if (this.onFrame) this.onFrame(this.massId, this.cursorMs, this.follow);
    },
    onSeek(event) {
      this.cursorMs = Number(event.target.value);
      this.emitCursor();
    },
    toggle() {
      if (this.playing) this.stop();
      else this.start();
    },
    start() {
      if (!this.span) return;
      if (this.cursorMs >= this.span.end) this.cursorMs = this.span.start;
      this.playing = true;
      this.lastFrame = window.performance.now();
      const step = (now) => {
        if (!this.playing) return;
        const delta = (now - this.lastFrame) / 1000;
        this.lastFrame = now;
        this.cursorMs = Math.min(this.cursorMs + delta * this.msPerSecond, this.span.end);
        this.emitCursor();
        if (this.cursorMs >= this.span.end) {
          this.stop();
          return;
        }
        this.raf = window.requestAnimationFrame(step);
      };
      this.raf = window.requestAnimationFrame(step);
    },
    stop() {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.raf = null;
      this.playing = false;
    },
  },
};
</script>

<style scoped>
/* left & bottom dari :style — atribusi Leaflet duduk di 17px paling bawah. */
.trail-scrubber {
  position: absolute;
  right: 12px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  font-size: 12px;
}

.scrub-btn {
  border: 0;
  border-radius: 6px;
  background: #ff69b4;
  color: #ffffff;
  font-weight: 700;
  padding: 4px 14px;
  flex: 0 0 auto;
}

/* Sekunder terhadap Play, biar tombol utamanya tetap kelihatan satu. */
.scrub-exit {
  background: #e5e7eb;
  color: #374151;
  padding: 4px 10px;
}

.scrub-range {
  flex: 1 1 auto;
  min-width: 0;
}

.scrub-time {
  flex: 0 0 auto;
  color: #374151;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.scrub-speed {
  flex: 0 0 auto;
  width: 72px;
  height: 28px;
  padding: 0 6px;
  font-size: 12px;
}

.scrub-follow {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  white-space: nowrap;
}

@media (max-width: 575px) {
  .trail-scrubber {
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 8px;
  }

  .scrub-speed {
    width: 58px;
  }

  .scrub-range {
    flex: 1 1 100%;
    order: 3;
  }
}
</style>
